import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/Header';
import { environment } from '../../config/environment';
import './JobDetails.css';

// ===== ANIMATION VARIANTS =====
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut', delay: i * 0.08 },
  }),
};

const FloatingParticles = () => {
  const particles = React.useMemo(() =>
    Array.from({ length: 15 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 12 + 8,
      delay: Math.random() * 5,
      drift: Math.random() * 50 - 25,
    })), []
  );

  return (
    <div className="jd-particles">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="jd-particle"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
          }}
          animate={{
            y: [0, -50, 0],
            x: [0, p.drift, 0],
            opacity: [0, 0.4, 0],
            scale: [0.3, 1.2, 0.3],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
};

const JobDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  // Use ID from URL params, or from location state if passed
  const jobId = id || location.state?.job?.id;

  const [job, setJob] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!jobId) {
        setIsLoading(false);
        setError('Job ID not found.');
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        // FIX: Properly build the URL with the ID at the end
        let url = `${environment.baseUrl}${environment.endpoints.getJobDetailForView}`;
        // Ensure the URL ends with a slash and the ID
        if (!url.endsWith('/')) {
          url += '/';
        }
        url += jobId;

        console.log('Fetching job details from:', url); // For debugging

        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        // Check if response is JSON, otherwise throw error
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Server returned a non-JSON response (possible 404 or server error).');
        }

        const data = await response.json();

        if (!response.ok || data?.response !== true) {
          throw new Error(data?.message || 'Failed to fetch job details.');
        }

        setJob(data.data);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message || 'Unable to load job details. Please try again.');
        setJob(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobDetails();
  }, [jobId]);

  const handleBackToJobs = () => {
    navigate('/job-listings');
  };

  const handleApplyNow = () => {
    navigate('/job-application', { state: { jobId: job.id, jobTitle: job.title } });
  };

  if (isLoading) {
    return (
      <div className="job-details-page">
        <Header />
        <div className="jd-loading-container">
          <FloatingParticles />
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="jd-loading-content"
          >
            <div className="jd-loader-ring">
              <div className="jd-loader-ring-inner" />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="jd-loading-text"
            >
              Loading Details<span className="jd-dot-1">.</span><span className="jd-dot-2">.</span><span className="jd-dot-3">.</span>
            </motion.p>
          </motion.div>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="job-details-page">
        <Header />
        <div className="jd-error-container">
          <FloatingParticles />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="jd-error-content"
          >
            <motion.div
              animate={{ rotate: [0, -10, 10, -5, 0], scale: [1, 1.1, 1.1, 1] }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="jd-error-icon"
            >
              ⚠️
            </motion.div>
            <h3>Unable to load job details</h3>
            <p>{error || 'Job details not found. Please check the URL or try again later.'}</p>
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleBackToJobs}
              className="jd-retry-btn"
            >
              Back to Jobs
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Prepare info cards data from API
  const infoCards = [
    {
      icon: <svg className="jd-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>,
      label: 'Location',
      value: job.location || 'Not Specified'
    },
    {
      icon: <svg className="jd-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
      label: 'Experience',
      value: `${job.experience || 0} Years`
    },
    {
      icon: <svg className="jd-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
      label: 'Openings',
      value: job.openings || 0
    },
    {
      icon: <svg className="jd-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" /></svg>,
      label: 'Education',
      value: job.education || 'Not Specified'
    },
    {
      icon: <svg className="jd-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>,
      label: 'Designation',
      value: job.designation || 'General'
    },
    {
      icon: <svg className="jd-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>,
      label: 'Job Type',
      value: job.job_type || 'Full Time'
    },
    {
      icon: <svg className="jd-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
      label: 'Salary',
      value: job.salary_text || 'Negotiable'
    },
    {
      icon: <svg className="jd-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
      label: 'Deadline',
      value: job.deadline ? new Date(job.deadline).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Open'
    },
  ];

  return (
    <div className="job-details-page">
      <Header />
      <FloatingParticles />

      <main className="jd-main">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
          className="jd-topbar"
        >
          <button className="jd-back-link" onClick={handleBackToJobs}>
            <svg className="jd-back-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Jobs
          </button>
        </motion.div>

        {/* Hero Section */}
        <motion.div
          className="jd-hero"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          <div className="jd-hero-content">
            <motion.span
              className="jd-hero-badge"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              {job.job_type || 'Full Time'}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.25 }}
            >
              {job.title}
            </motion.h1>
            <motion.p className="jd-hero-location">
              <svg className="jd-hero-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {job.location || 'Patna, Bihar'}
            </motion.p>
          </div>
        </motion.div>

        {/* Info Cards Grid */}
        <motion.div
          className="jd-info-grid"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={2}
        >
          {infoCards.map((card, i) => (
            <motion.div
              key={card.label}
              className="jd-info-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.04 }}
              whileHover={{ y: -4, boxShadow: '0 12px 32px rgba(59,130,246,0.15)' }}
            >
              <span className="jd-info-icon">{card.icon}</span>
              <div className="jd-info-text">
                <span className="jd-info-label">{card.label}</span>
                <span className="jd-info-value">{card.value}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Job Description */}
        <motion.div
          className="jd-section-card"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={3}
        >
          <div className="jd-section-header">
            <span className="jd-section-icon">
              <svg className="jd-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            </span>
            <h2>Job Description</h2>
          </div>
          <p className="jd-section-text">
            {job.description && job.description !== 'No description available.'
              ? job.description
              : 'Join our team and grow your career with us. We are looking for a dedicated professional to fill this position.'}
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          className="jd-actions"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={4}
        >
          <motion.button
            className="jd-apply-btn"
            // onClick={handleApplyNow}
            whileHover={{ scale: 1.03, y: -2, boxShadow: '0 8px 28px rgba(29,78,216,0.4)' }}
            whileTap={{ scale: 0.95 }}
          >
            Apply Now →
          </motion.button>
          <motion.button
            className="jd-save-btn"
            onClick={handleBackToJobs}
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            Back to Jobs
          </motion.button>
        </motion.div>
      </main>
    </div>
  );
};

export default JobDetails;